import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, Form } from "react-bootstrap";
import schoolApi from "../../api/schoolApi";

const ReviewTaskModal = ({ show, onHide, user, onApprove, onReject, onDownload }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [adminComment, setAdminComment] = useState("");

  const isImage = (fileName) => {
    if (!fileName) return false;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  };

  const isVideo = (fileName) => {
    if (!fileName) return false;
    return /\.(mp4|webm|ogg)$/i.test(fileName);
  };

  useEffect(() => {
    if (show) {
      setAdminComment(user?.admin_comment || "");
    }
  }, [show, user]);

  useEffect(() => {
    console.log("useEffect triggered with show:", show, "user.file_id:", user, "user.file_title:", user?.file_title);
    if (show && user?.file_id && (isImage(user.file_title) || isVideo(user.file_title))) {
      const fetchPreview = async () => {
        try {
          const response = await schoolApi.downloadFiles(user.file_id);
            console.log("responseprev=>",response);
            const blob = new Blob([response], { type: response.type });
          console.log("blob=>",blob);
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        } catch (error) {
          console.error("Error fetching preview:", error);
        }
      };
      fetchPreview();
    }
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    };
  }, [show, user?.file_id, user?.file_title]);

  if (!user) return null;

  const statusColors = {
    Submitted: 'primary',
    Approved: 'success',
    Rejected: 'danger',
    Pending: 'warning'
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable className="review-modal">
      <Modal.Header closeButton className="border-0 pb-0 bg-white">
        <Modal.Title className="fw-bold d-flex align-items-center">
          <div className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '50px', height: '50px' }}>
            <i className="fa-solid fa-user-shield fs-4"></i>
          </div>
          <div>
            <div className="fs-5 text-dark">Submission from {user.name}</div>
            <Badge bg={statusColors[user.status] || 'secondary'} className="fw-normal rounded-pill px-3" style={{ fontSize: '0.7rem' }}>
              {user.status?.toUpperCase() || 'PENDING'}
            </Badge>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4 bg-white">
        <div className="mb-4">
          <h6 className="fw-bold text-muted mb-3 text-uppercase small" style={{ letterSpacing: '1px' }}>Teacher Note </h6>
          <div className="p-4 rounded-4 shadow-sm border bg-light bg-opacity-50" style={{ minHeight: '100px', fontSize: '0.95rem', color: '#444' }} dangerouslySetInnerHTML={{ __html: user.description || '<span class="text-muted italic">No note submitted.</span>' }}>
          </div>
        </div>

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold text-muted mb-0 text-uppercase small" style={{ letterSpacing: '1px' }}>Attached File Preview</h6>
            {user.file_id && (
              <Button variant="outline-primary" size="sm" className="rounded-pill px-3 fw-bold" onClick={() => onDownload(user.file_id, user.file_title)}>
                <i className="fa-solid fa-download me-1"></i> Download
              </Button>
            )}
          </div>

          {user.file_id ? (
            <div className="preview-container bg-dark rounded-4 overflow-hidden shadow-lg border-0 position-relative">
              {isImage(user.file_title) ? (
                previewUrl ? (
                  <img 
                    src={previewUrl} 
                    alt="Submission preview" 
                    className="img-fluid d-block mx-auto" 
                    style={{ maxHeight: '500px', objectFit: 'contain', width: '100%' }} 
                  />
                ) : <div className="p-5 text-center text-white opacity-50"><i className="fa-solid fa-spinner fa-spin fs-2"></i></div>
              ) : isVideo(user.file_title) ? (
                previewUrl ? (
                  <video 
                    src={previewUrl} 
                    controls 
                    className="w-100 d-block" 
                    style={{ maxHeight: '500px' }} 
                  />
                ) : <div className="p-5 text-center text-white opacity-50"><i className="fa-solid fa-spinner fa-spin fs-2"></i></div>
              ) : (
                <div className="p-5 text-center text-white opacity-75">
                  <i className="fa-solid fa-file-circle-question fs-1 mb-3 d-block"></i>
                  <p className="mb-0">No visual preview available for this file type.</p>
                  <small className="text-muted">{user.file_title}</small>
                </div>
              )}
            </div>
          ) : (
            <div className="p-5 bg-light rounded-4 text-center border-dashed">
              <i className="fa-solid fa-file-excel text-muted fs-1 mb-3 d-block"></i>
              <span className="text-muted fw-bold">No attachment provided.</span>
            </div>
          )}
        </div>

        {(user.status === 'Submitted' || user.admin_comment) && (
          <div className="mb-3">
            <h6 className="fw-bold text-muted mb-3 text-uppercase small" style={{ letterSpacing: '1px' }}>Admin Feedback / Comment</h6>
            {user.status === 'Submitted' ? (
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter feedback or reason for rejection/approval..."
                className="rounded-4 border-2 shadow-sm p-3"
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                style={{ fontSize: '0.9rem', resize: 'none' }}
              />
            ) : (
              <div className="p-4 rounded-4 shadow-sm border bg-info bg-opacity-10 text-info border-info border-opacity-25" style={{ fontSize: '0.95rem' }}>
                <i className="fa-solid fa-comment-dots me-2"></i>
                {user.admin_comment || "No feedback provided."}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      {user.status === 'Submitted' && (
        <Modal.Footer className="border-0 bg-white p-4 pt-0">
          <div className="d-flex gap-3 w-100">
            <Button variant="outline-danger" className="flex-grow-1 py-3 rounded-3 shadow-sm fw-bold border-2" onClick={() => onReject(user.user_id, adminComment)}>
              <i className="fa-solid fa-circle-xmark me-2"></i> REJECT
            </Button>
            <Button variant="success" className="flex-grow-1 py-3 rounded-3 shadow-sm fw-bold border-0" onClick={() => onApprove(user.user_id, adminComment)}>
              <i className="fa-solid fa-circle-check me-2"></i> APPROVE
            </Button>
          </div>
        </Modal.Footer>
      )}
      <style dangerouslySetInnerHTML={{ __html: `
        .review-modal .modal-content { border-radius: 1.5rem; overflow: hidden; border: none; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); }
        .border-dashed { border: 2px dashed #e2e8f0; }
        .preview-container { min-height: 200px; display: flex; align-items: center; justify-content: center; }
      `}} />
    </Modal>
  );
};

export default ReviewTaskModal;
